import React from 'react';
import { Star, MapPin, CheckCircle2, Heart } from 'lucide-react';
import { Companion } from '../types';
import { isDemoCompanionProfile } from '../data/companionsData';
import { useLanguage } from '../i18n/LanguageContext';

interface CompanionCardProps {
  companion: Companion;
  isWishlisted: boolean;
  onToggleWishlist: (id: string, e: React.MouseEvent) => void;
  onSelect: (companion: Companion) => void;
  onBookNow: (companion: Companion, e: React.MouseEvent) => void;
  distanceKm?: number;
}

export const CompanionCard: React.FC<CompanionCardProps> = ({
  companion,
  isWishlisted,
  onToggleWishlist,
  onSelect,
  onBookNow,
  distanceKm,
}) => {
  const { t } = useLanguage();
  const isDemo = isDemoCompanionProfile(companion);

  return (
    <div
      id={`companion-card-${companion.id}`}
      onClick={() => onSelect(companion)}
      className="group bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl hover:border-pink-200 transition-all duration-300 flex flex-col cursor-pointer"
    >
      {/* Image Container */}
      <div className="relative aspect-[4/4.2] w-full overflow-hidden bg-slate-100">
        <img
          src={companion.image}
          alt={companion.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          referrerPolicy="no-referrer"
        />

        {/* Availability Badge */}
        {!isDemo && (
          <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-md text-emerald-300 text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1.5 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>Available</span>
          </div>
        )}

        {/* Wishlist Heart button */}
        <button
          id={`wishlist-btn-${companion.id}`}
          onClick={(e) => onToggleWishlist(companion.id, e)}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
            isWishlisted
              ? 'bg-pink-500 text-white shadow-md'
              : 'bg-white/80 hover:bg-white text-slate-600 hover:text-pink-600 backdrop-blur-xs'
          }`}
          title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <Heart className={`w-4 h-4 ${isWishlisted ? 'fill-white' : ''}`} />
        </button>

        {/* Quick book button overlay on hover */}
        <div className="absolute inset-x-3 bottom-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <button
            id={`quick-book-${companion.id}`}
            onClick={(e) => onBookNow(companion, e)}
            className="w-full py-2 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white text-xs font-bold rounded-xl shadow-lg shadow-pink-600/40 transition-transform active:scale-95"
          >
            Quick Book ✨
          </button>
        </div>
      </div>

      {/* Card Content matching exact screenshot */}
      <div className="p-4 flex flex-col flex-1 justify-between">
        <div>
          {/* Name, Age & Verified badge */}
          <div className="flex items-center justify-between gap-1.5 mb-1.5 flex-wrap">
            <h3 className="font-bold text-slate-900 text-base group-hover:text-pink-600 transition-colors truncate">
              {companion.displayName || companion.name}, {companion.age}
            </h3>
            <div className="flex items-center gap-1 shrink-0">
              {companion.isDriverApproved && (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-bold shrink-0"
                  title="Verified Licensed Driver"
                >
                  <span>🚗 Driver</span>
                </span>
              )}
              {companion.supportedOccasions?.includes('Tourist Guide') && (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold shrink-0"
                  title="Tourist Guide"
                >
                  <span>🏛️ Guide</span>
                </span>
              )}
              {!isDemo && companion.isVerified && (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 text-[10px] font-bold shrink-0"
                  title="Government ID Verified ✓ (Legal name kept private)"
                >
                  <CheckCircle2 className="w-3 h-3 text-sky-500 fill-sky-500/10" />
                  <span>{t('companions.verifiedBadge')} ✓</span>
                </span>
              )}
            </div>
          </div>

          {/* Rating & City Location */}
          <div className="flex items-center gap-3 text-xs text-slate-500 mb-1.5">
            <div className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              <span className="font-bold text-slate-700">{companion.rating}</span>
              <span className="text-slate-400">({companion.reviewsCount})</span>
            </div>

            <div className="flex items-center gap-1 min-w-0">
              <MapPin className="w-3.5 h-3.5 text-pink-500 shrink-0" />
              <span className="truncate">{companion.city}</span>
              {distanceKm !== undefined && (
                <span className="text-[10px] font-extrabold text-pink-700 bg-pink-50 px-1.5 py-0.5 rounded-md border border-pink-100 shrink-0">
                  {distanceKm} km
                </span>
              )}
            </div>
          </div>

          {/* Availability */}
          {companion.availability && (
            <div className="text-[11px] font-medium text-purple-700 bg-purple-50/70 px-2 py-0.5 rounded-md inline-block mb-2 truncate max-w-full">
              🕒 {companion.availability}
            </div>
          )}
        </div>

        {/* Bottom Price & Heart indicator */}
        <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-extrabold text-slate-900">
              ₹{companion.hourlyRate}
            </span>
            <span className="text-xs text-slate-500">/hour</span>
          </div>

          {/* Mini Action Indicator */}
          <div className="w-7 h-7 rounded-full bg-pink-50 flex items-center justify-center text-pink-500 group-hover:bg-pink-600 group-hover:text-white transition-colors">
            <Heart className="w-3.5 h-3.5" />
          </div>
        </div>
      </div>
    </div>
  );
};
